import React, { useState, useEffect } from 'react';
import { CheckCircle2, Upload, FileText } from 'lucide-react';
import { Button, ICON_PX } from '../ui';

/**
 * Reusable completion form — renders the appropriate input based on completion type.
 * Used in both CompletionModal (dialog context) and WorkflowSidebar (inline context).
 *
 * @param {string} completionType - 'checkbox' | 'checkbox_note' | 'checkbox_linked'
 * @param {function} onSubmit - Called with { note, linkedOutputs } when valid and submitted
 * @param {boolean} disabled - Disable all inputs
 * @param {boolean} compact - Use compact spacing (for sidebar)
 */
function CompletionForm({ completionType = 'checkbox', onSubmit, disabled = false, compact = false }) {
  const [note, setNote] = useState('');
  const [linkedFiles, setLinkedFiles] = useState([]);
  const [confirmAssignment, setConfirmAssignment] = useState(false);

  // Reset on type change
  useEffect(() => {
    setNote('');
    setLinkedFiles([]);
    setConfirmAssignment(false);
  }, [completionType]);

  const isValid = () => {
    if (completionType === 'checkbox') return true;
    if (completionType === 'checkbox_note') return note.length >= 10;
    if (completionType === 'checkbox_linked') return linkedFiles.length > 0 || confirmAssignment;
    return true;
  };

  const handleSubmit = () => {
    if (!isValid()) return;
    onSubmit?.({
      note: completionType === 'checkbox_note' ? note : undefined,
      linkedOutputs: completionType === 'checkbox_linked' ? linkedFiles : undefined,
    });
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    setLinkedFiles((prev) => [...prev, ...files.map((f) => ({ name: f.name, type: f.type, size: f.size }))]);
  };

  const gap = compact ? 'space-y-2' : 'space-y-3';

  return (
    <div className={gap}>
      {/* Checkbox type — simple confirm */}
      {completionType === 'checkbox' && (
        <p className="text-body-sm text-text-secondary">
          Confirm completion. No additional documentation required.
        </p>
      )}

      {/* Checkbox + Note */}
      {completionType === 'checkbox_note' && (
        <div>
          <label className="block text-caption font-bold text-text-muted uppercase tracking-wider mb-1">
            Completion Note <span className="text-red-critical">*</span>
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Describe what was done (minimum 10 characters)..."
            rows={compact ? 3 : 4}
            disabled={disabled}
            className="w-full rounded-md border border-nn-border bg-nn-white px-3 py-2 text-body text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/40 resize-none"
          />
          <span className={`text-caption ${note.length < 10 ? 'text-red-critical' : 'text-text-muted'}`}>
            {note.length}/10 minimum
          </span>
        </div>
      )}

      {/* Checkbox + Linked Output */}
      {completionType === 'checkbox_linked' && (
        <>
          <p className="text-body-sm text-text-secondary">
            Provide at least one form of evidence.
          </p>

          {/* File upload */}
          <div>
            <label className={`flex items-center justify-center gap-2 ${compact ? 'p-3' : 'p-4'} rounded-lg border-2 border-dashed border-nn-border hover:border-aqua cursor-pointer transition-colors`}>
              <Upload size={ICON_PX.lg} className="text-text-muted" />
              <span className="text-body-sm text-text-muted">Click or drag to upload</span>
              <input type="file" multiple className="hidden" onChange={handleFileSelect} disabled={disabled} />
            </label>
            {linkedFiles.length > 0 && (
              <div className="mt-1.5 space-y-1">
                {linkedFiles.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-body-sm text-text-secondary p-1.5 bg-nn-bg-subtle rounded">
                    <FileText size={ICON_PX.sm} />
                    <span className="truncate">{f.name}</span>
                    <button
                      type="button"
                      onClick={() => setLinkedFiles((prev) => prev.filter((_, idx) => idx !== i))}
                      className="ml-auto text-text-muted hover:text-red-critical"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Confirm assignment */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="cf-confirm"
              checked={confirmAssignment}
              onChange={(e) => setConfirmAssignment(e.target.checked)}
              className="rounded border-nn-border"
              disabled={disabled}
            />
            <label htmlFor="cf-confirm" className="text-body-sm text-text-secondary">
              Confirm assignment was sent
            </label>
          </div>
        </>
      )}

      {/* Submit button */}
      <Button
        variant="success"
        size="sm"
        className="w-full"
        disabled={!isValid() || disabled}
        onClick={handleSubmit}
      >
        <CheckCircle2 size={ICON_PX.md} className="mr-1" /> Complete
      </Button>
    </div>
  );
}

export default CompletionForm;
